import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import {
  defaultDictationVocabulary,
  type DictationVocabularySettings,
} from "@/dictation-interactions";
import { CloseIcon } from "@/components/Icons";

// Pure shared vocabulary fields. The desktop surface uses this directly so it
// does not pull native keyboard configuration into its route bundle; native
// callers can provide an after-save adapter.
const DictationVocabularyFields = ({
  afterSave,
}: {
  afterSave?: (next: DictationVocabularySettings) => void;
}) => {
  const { t } = useTranslation();
  const dictationVocabulary = useAppStore(
    (state) => state.dictationVocabulary ?? defaultDictationVocabulary,
  );
  const setDictationVocabulary = useAppStore(
    (state) => state.setDictationVocabulary,
  );
  const [vocabularyFrom, setVocabularyFrom] = useState("");
  const [vocabularyTo, setVocabularyTo] = useState("");

  const save = (next: DictationVocabularySettings) => {
    setDictationVocabulary(next);
    afterSave?.(next);
  };

  const addEntry = () => {
    const from = vocabularyFrom.trim();
    const to = vocabularyTo.trim();
    if (!from || !to) return;
    save({
      entries: [...dictationVocabulary.entries, { from, to }],
    });
    setVocabularyFrom("");
    setVocabularyTo("");
  };

  return (
    <div className="space-y-3">
      {dictationVocabulary.entries.map((entry, index) => (
        <div
          key={`${entry.from}:${index}`}
          className="flex items-center gap-3 rounded-2xl glass-tile px-4 py-3"
        >
          <span className="flex-1 min-w-0 text-sm text-[var(--text-primary)] truncate">
            {entry.from}
            <span className="text-[var(--text-tertiary)] mx-2">→</span>
            {entry.to}
          </span>
          <button
            data-action-id="settings.dictationVocabulary.remove"
            aria-label={t("settings.vocabularyRemove")}
            onClick={() =>
              save({
                entries: dictationVocabulary.entries.filter(
                  (_, entryIndex) => entryIndex !== index,
                ),
              })
            }
            className="shrink-0 w-8 h-8 rounded-full bg-[var(--fill-1)] hover:bg-[var(--fill-2)] flex items-center justify-center text-[var(--text-secondary)] transition-colors"
          >
            <CloseIcon size={14} />
          </button>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <input
          data-action-id="input.edit"
          value={vocabularyFrom}
          onChange={(e) => setVocabularyFrom(e.target.value)}
          placeholder={t("settings.vocabularyFromPlaceholder")}
          className="flex-1 min-w-0 h-11 px-4 rounded-2xl bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-sm outline-none shadow-sm"
        />
        <input
          data-action-id="input.edit"
          value={vocabularyTo}
          onChange={(e) => setVocabularyTo(e.target.value)}
          placeholder={t("settings.vocabularyToPlaceholder")}
          className="flex-1 min-w-0 h-11 px-4 rounded-2xl bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-sm outline-none shadow-sm"
        />
        <button
          data-action-id="settings.dictationVocabulary.add"
          onClick={addEntry}
          disabled={!vocabularyFrom.trim() || !vocabularyTo.trim()}
          className="shrink-0 h-11 px-4 rounded-2xl bg-[var(--accent)] text-[var(--text-inverse)] font-semibold text-sm transition-all active:scale-[0.97] disabled:opacity-40"
        >
          {t("settings.vocabularyAdd")}
        </button>
      </div>
    </div>
  );
};

export default DictationVocabularyFields;
